import React, { useEffect, useState } from "react";
import { apiAdmin } from "../../lib/api";
import { Plus, Trash2, Edit3, X, FolderTree } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { value: "3d", label: "Figuras 3D" },
  { value: "papeleria", label: "Papelería" },
];

const empty = { name: "", slug: "", section: "3d", order: 0, active: true };

const slugify = (s) =>
  s.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const AdminCategories = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => apiAdmin.get("/categories/all").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, slug: c.slug, section: c.section, order: c.order, active: c.active });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name) { toast.error("Nombre requerido"); return; }
    const payload = { ...form, slug: form.slug || slugify(form.name) };
    try {
      if (editing) await apiAdmin.put(`/categories/${editing.id}`, payload);
      else await apiAdmin.post("/categories", payload);
      toast.success(editing ? "Actualizada" : "Creada");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar categoría? Los productos con esta categoría seguirán existiendo.")) return;
    await apiAdmin.delete(`/categories/${id}`);
    toast.success("Eliminada");
    load();
  };

  const grouped = { "3d": items.filter((i) => i.section === "3d"), papeleria: items.filter((i) => i.section === "papeleria") };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-end justify-between mb-10">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-yolk">─ Categorías ─</span>
          <h1 className="mt-2 font-heading text-4xl font-bold text-brand-ink">Administrar categorías</h1>
          <p className="text-gray-600 mt-1 text-sm">Estas categorías aparecen como filtros en el catálogo público.</p>
        </div>
        <button onClick={openNew} data-testid="category-new-btn" className="btn-chunky inline-flex items-center gap-2 bg-mint-deep text-white px-5 py-3 text-sm">
          <Plus className="h-4 w-4" /> Nueva categoría
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {SECTIONS.map((sec) => (
          <div key={sec.value} className="bg-white border-2 border-black rounded-3xl overflow-hidden">
            <div className="px-6 py-4 bg-mint-deep text-white flex items-center gap-3">
              <FolderTree className="h-5 w-5" />
              <h2 className="font-heading font-bold">{sec.label}</h2>
              <span className="ml-auto text-xs bg-white/20 px-2 py-0.5 rounded-full">{grouped[sec.value].length}</span>
            </div>
            <div className="divide-y-2 divide-black/10">
              {grouped[sec.value].length === 0 ? (
                <p className="p-6 text-center text-gray-400 text-sm">Sin categorías</p>
              ) : grouped[sec.value].map((c) => (
                <div key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center gap-4 p-4 hover:bg-yolk-light/40">
                  <div className="flex-1">
                    <p className="font-heading font-bold text-brand-ink">{c.name}</p>
                    <p className="text-xs text-gray-500">
                      <code className="bg-mint-light px-1.5 py-0.5 rounded">{c.slug}</code>
                      <span className="ml-2">orden: {c.order}</span>
                      {!c.active && <span className="ml-2 text-red-500 font-bold">INACTIVA</span>}
                    </p>
                  </div>
                  <button onClick={() => openEdit(c)} data-testid={`category-edit-${c.id}`} className="btn-soft inline-flex items-center gap-1 bg-white px-3 py-1.5 text-xs">
                    <Edit3 className="h-3 w-3" /> Editar
                  </button>
                  <button onClick={() => remove(c.id)} data-testid={`category-delete-${c.id}`} className="btn-soft inline-flex items-center px-2 py-1.5 bg-red-50 text-red-500">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <div className="bg-white border-2 border-black rounded-3xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b-2 border-black">
              <h2 className="font-heading text-xl font-bold">{editing ? "Editar" : "Nueva"} categoría</h2>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-yolk-light rounded-full"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold mb-2">Nombre *</label>
                <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value, slug: form.slug || slugify(e.target.value)})} data-testid="category-form-name" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-mint-deep" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold mb-2">Slug (URL)</label>
                <input value={form.slug} onChange={(e) => setForm({...form, slug: slugify(e.target.value)})} data-testid="category-form-slug" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:border-mint-deep" />
                <p className="text-xs text-gray-500 mt-1">Se genera del nombre si se deja vacío</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold mb-2">Sección</label>
                  <select value={form.section} onChange={(e) => setForm({...form, section: e.target.value})} data-testid="category-form-section" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm bg-white">
                    {SECTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] font-bold mb-2">Orden</label>
                  <input type="number" value={form.order} onChange={(e) => setForm({...form, order: Number(e.target.value)})} data-testid="category-form-order" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm" />
                </div>
              </div>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})} data-testid="category-form-active" className="h-4 w-4" />
                <span className="text-sm">Activa (visible en el catálogo)</span>
              </label>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowForm(false)} className="btn-chunky flex-1 bg-white text-black py-3 text-sm">Cancelar</button>
                <button onClick={submit} data-testid="category-form-submit" className="btn-chunky flex-1 bg-mint-deep text-white py-3 text-sm">Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
